import { UserProfile, BusinessRuleResult, InteractionRule } from '../types';
import { adminService } from '@/core/services/AdminService';
import { blockService } from '@/core/services/BlockService';

export class UserInteractionRules {
  private static interactionRules: InteractionRule[] = [
    {
      sourceRole: 'casual',
      targetRole: 'casual',
      condition: () => false, // Casuals não podem interagir entre si
    },
    {
      sourceRole: 'serious',
      targetRole: 'serious', 
      condition: () => false, // Serious não podem interagir entre si
    },
    {
      sourceRole: 'sponsor',
      targetRole: 'sponsored',
      condition: () => true, // Sponsors podem interagir com sponsored
    },
    {
      sourceRole: 'sponsored',
      targetRole: 'sponsor',
      condition: () => true, // Sponsored podem interagir com sponsors
    },
    {
      sourceRole: 'casual',
      targetRole: 'serious',
      condition: () => true, // Casual pode interagir com serious
    },
    {
      sourceRole: 'serious',
      targetRole: 'casual',
      condition: () => true, // Serious pode interagir com casual
    }
  ];

  static async canInteractWith(sourceUser: UserProfile, targetUser: UserProfile): Promise<BusinessRuleResult> {
    // Verificar bloqueio PRIMEIRO (antes de tudo, incluindo admin)
    const hasBlock = await blockService.isAnyBlockBetween(sourceUser.id, targetUser.id);
    if (hasBlock) {
      return {
        allowed: false,
        reason: 'Interação bloqueada',
        metadata: { blocked: true }
      };
    }

    // Admins bypassa todas as restrições
    const isAdmin = await adminService.isAdmin(sourceUser.id);
    if (isAdmin) {
      return {
        allowed: true,
        reason: 'Administrador pode interagir com qualquer usuário',
        metadata: { adminBypass: true }
      };
    }

    // Usuários hidden não podem interagir (exceto se o source for Joia 99+)
    if (targetUser.hidden) {
      return {
        allowed: false,
        reason: 'Usuário alvo está oculto'
      };
    }

    if (sourceUser.hidden) {
      return {
        allowed: false,
        reason: 'Usuário não pode interagir enquanto estiver oculto'
      };
    }

    // Verificar regras de role
    if (sourceUser.role && targetUser.role) {
      const applicableRule = this.interactionRules.find(rule => 
        rule.sourceRole === sourceUser.role && rule.targetRole === targetUser.role
      );

      if (applicableRule) {
        const canInteract = applicableRule.condition(sourceUser, targetUser);
        return {
          allowed: canInteract,
          reason: canInteract 
            ? `Interação permitida entre ${sourceUser.role} e ${targetUser.role}`
            : `Interação não permitida entre ${sourceUser.role} e ${targetUser.role}`,
          metadata: { 
            sourceRole: sourceUser.role, 
            targetRole: targetUser.role 
          }
        };
      }
    }

    // Se não há regra específica, permite interação
    return {
      allowed: true,
      reason: 'Nenhuma restrição encontrada para esta interação'
    };
  }

  static async canSendMessage(sourceUser: UserProfile, targetUser: UserProfile): Promise<BusinessRuleResult> {
    // Primeiro verifica se podem interagir (já inclui verificação de bloqueio)
    const interactionResult = await this.canInteractWith(sourceUser, targetUser);
    if (!interactionResult.allowed) {
      return interactionResult;
    }

    // Verificações adicionais para mensagens
    if (sourceUser.plan_type === 'free') {
      // Usuários free têm limitações adicionais
      return {
        allowed: true,
        reason: 'Mensagem permitida (verificar limites diários)',
        metadata: { requiresLimitCheck: true }
      };
    }

    return {
      allowed: true,
      reason: 'Mensagem permitida'
    };
  }

  static async canViewProfile(viewerUser: UserProfile, profileUser: UserProfile): Promise<BusinessRuleResult> {
    // Verificar bloqueio PRIMEIRO
    const hasBlock = await blockService.isAnyBlockBetween(viewerUser.id, profileUser.id);
    if (hasBlock) {
      return {
        allowed: false,
        reason: 'Perfil bloqueado',
        metadata: { blocked: true }
      };
    }

    // Admins podem ver qualquer perfil
    const isAdmin = await adminService.isAdmin(viewerUser.id);
    if (isAdmin) {
      return {
        allowed: true,
        reason: 'Administrador pode ver qualquer perfil',
        metadata: { adminBypass: true }
      };
    }

    // Usuários hidden não podem ser vistos
    if (profileUser.hidden) {
      return {
        allowed: false,
        reason: 'Perfil está oculto'
      };
    }

    // Aplicar regras de interação para visualização
    return await this.canInteractWith(viewerUser, profileUser);
  }
}
